<template>
  <div :class="$gStyle('mt-6')">
    <!-- EXTENDED HEADER -->
    <div :class="$gStyle('mt-4 py-6 px-2')">
      <span :class="$gStyle('font-bold')">{{
        t('serialNumber.formInformation')
      }}</span>
    </div>

    <!-- Search Area -->
    <div
      :class="$gStyle('flex my-6')"
      :style="
        displayBreakpoint === 'isMobile'
          ? 'flex-direction: column;'
          : 'gap: 16px'
      "
    >
      <patternlib-textinput
        id="searchInput"
        :label="t('spareparts.searchLabel')"
        :placeholder="t('spareparts.searchPlaceholder')"
        @lhChange="
          (ev) => {
            searchFilter = ev.detail.value ? ev.detail.value?.toString() : '';
            if (!searchFilter) {
              //force update on empty string => 'clear' event on clear button is stopped from propagating by patternlib
              updateFilter('');
            }
          }
        "
        @keyup.enter="updateFilter(searchFilter)"
      >
      </patternlib-textinput>
      <patternlib-button
        style="align-self: flex-end; height: 48px"
        :width="displayBreakpoint === 'isMobile' ? '100%' : ''"
        :label="t('spareparts.searchButton').toLocaleUpperCase()"
        type="secondary"
        @click="updateFilter(searchFilter)"
      ></patternlib-button>
    </div>

    <!-- POSITION TABLE -->
    <patternlib-table-base
      :class="{ isMobile: displayBreakpoint === 'isMobile' }"
      @vue:mounted="() => observeHeadSize()"
    >
      <!-- Table Header -->
      <tr slot="head" id="head">
        <template v-if="displayBreakpoint !== 'isMobile'">
          <th id="headerSparePart" ref="header">
            {{ t('spareparts.position') }}
          </th>
          <th>{{ t('spareparts.description') }}</th>
          <th>{{ t('positionModal.articleNumber') }}</th>
          <th style="display: none">{{ t('spareparts.availability') }}</th>
          <th :style="{ display: enablePrices ? '' : 'none' }">
            {{ t('spareparts.price') }}
          </th>
          <th style="min-width: 200px">{{ t('spareparts.quantity') }}</th>
          <th></th>
        </template>
      </tr>

      <!-- Table Content -->
      <patternlib-table-base-row
        v-for="[_, hotspot] of getHotSpots().value"
        :key="hotspot.position"
        :show-sub-rows="
          getZPartsByPosition(hotspot.position!).value.length > 0 ||
          getVirtualGroupsByPositionByLevel(hotspot.position!, 1).value.length >
            0
        "
        :id="hotspot.articleNumber"
      >
        <!-- Position -->
        <patternlib-table-base-cell noBorder id="position"
          ><span v-html="getHighlightedPosition(hotspot.position)"></span>
        </patternlib-table-base-cell>
        <!-- Name -->
        <patternlib-table-base-cell noBorder id="name">
          <span v-html="getHighlightedName(hotspot.name)"></span
        ></patternlib-table-base-cell>
        <!-- ArticleNumber -->
        <patternlib-table-base-cell noBorder id="articleNumber"
          ><span
            v-html="getHighlightedArticleNumber(hotspot.articleNumber)"
          ></span>
        </patternlib-table-base-cell>
        <!-- IsInStock -->
        <patternlib-table-base-cell
          noBorder
          id="isInStock"
          :style="{ color: getColor(hotspot.stockType), display: 'none' }"
          ><span v-if="hotspot.stockType === 'AVAILABLE'">{{
            hotspot.stockText
          }}</span
          ><patternlib-tooltip v-else tooltip-position="center">
            <div
              slot="content"
              style="
                text-align: left;
                color: black;
                padding: 10px;
                width: 400px;
              "
            >
              {{ t('spareparts.outOfStock') }}
            </div>
            <div style="text-decoration: underline">
              {{ hotspot.stockText }}
            </div>
          </patternlib-tooltip></patternlib-table-base-cell
        >
        <!-- Price -->
        <patternlib-table-base-cell v-if="enablePrices" noBorder id="price">
          <div
            :style="{
              textAlign: 'start',
              fontWeight: displayBreakpoint === 'isMobile' ? 'bold' : 'normal'
            }"
          >
            {{
              hotspot.position &&
              getZPartsByPosition(hotspot.position).value.length > 0
                ? formatCurrency(
                    [
                      hotspot,
                      ...getZPartsByPosition(hotspot.position).value
                    ].reduce((a, b) => a + (b.priceGross ?? 0), 0),
                    hotspot.currency
                  )
                : formatCurrency(hotspot.priceGross, hotspot.currency)
            }}
          </div></patternlib-table-base-cell
        >
        <!-- Quantity -->
        <patternlib-table-base-cell noBorder id="quantity" width="200px">
          <patternlib-textinput
            @vue:mounted="
              hotspot.position && quantities?.set(hotspot.position, 1)
            "
            type="number"
            min="1"
            max="999"
            @lhChange="
              (event) =>
                quantities?.set(hotspot.position!, event.detail.value as number)
            "
            :value="(quantities?.get(hotspot.position!) || 1) * 1"
            style="min-width: 150px; display: block"
          >
          </patternlib-textinput
        ></patternlib-table-base-cell>
        <!-- Action -->
        <patternlib-table-base-cell noBorder id="action">
          <patternlib-button
            aria-label="plus"
            :disabled="!isMultiCompontsLoaded"
            :loading="!isMultiCompontsLoaded"
            @click="
              addArticle(<ExtendedOrderPosition>{
                articleName: hotspot.name,
                articleNumber: hotspot.articleNumber,
                qty: quantities.get(hotspot.position!) || 1,
                price: hotspot.priceGross || 0,
                currency: hotspot.currency,
                stockText: hotspot.stockText,
                stockType: hotspot.stockType,
                position: hotspot.position!
              })
            "
          >
            <patternlib-icon icon-name="plus" size="24px" slot="icon" />
          </patternlib-button>
        </patternlib-table-base-cell>

        <!-- Z-Parts & Virtual Groups Slot-->
        <patternlib-table-base
          slot="sub"
          class="subTable"
          v-if="
            hotspot.position &&
            (getZPartsByPosition(hotspot.position).value.length > 0 ||
              getVirtualGroupsByPositionByLevel(hotspot.position!, 1).value
                .length > 0)
          "
        >
          <patternlib-table-base-row noBorder>
            <patternlib-table-base-cell :colspan="999">
              {{
                getZPartsByPosition(hotspot.position).value.length > 0
                  ? t('spareparts.zPartInfoText')
                  : t('spareparts.virtualGroupInfoText')
              }}
              <br /><span style="font-weight: bold">{{
                getZPartsByPosition(hotspot.position).value.length > 0
                  ? t('spareparts.zPartAutoAdd')
                  : t('spareparts.virtualGroupPackageHint')
              }}</span>
            </patternlib-table-base-cell>
          </patternlib-table-base-row>

          <!-- Z-Parts-->
          <patternlib-table-base-row
            v-if="getZPartsByPosition(hotspot.position).value.length > 0"
            v-for="zPart in getZPartsByPosition(hotspot.position).value"
            noBorder
          >
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[0]?.toString() : undefined"
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[1]?.toString() : undefined"
              ><span v-html="getHighlightedName(zPart.name)"></span
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :colspan="2"
              :width="sizes ? sizes[2]?.toString() : undefined"
              ><span
                v-html="getHighlightedArticleNumber(zPart.articleNumber)"
              ></span>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :colspan="2"
              :width="sizes ? sizes[3]?.toString() : undefined"
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              v-if="enablePrices"
              noBorder
              :width="sizes ? sizes[4]?.toString() : undefined"
            >
              <div style="text-align: start">
                {{ formatCurrency(zPart.priceGross, zPart.currency) }}
              </div>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[5]?.toString() : undefined"
            >
              <patternlib-textinput
                @vue:mounted="
                  hotspot.position && quantities?.set(hotspot.position, 1)
                "
                type="number"
                max="999"
                :value="
                  (quantities?.get(hotspot.position!) || 1) *
                  (zPart.quantity || 1)
                "
                readonly
                disabled
              >
              </patternlib-textinput>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[6]?.toString() : undefined"
            ></patternlib-table-base-cell>
          </patternlib-table-base-row>

          <!-- Virtual Groups (First Level)-->
          <patternlib-table-base-row
            v-if="
              getVirtualGroupsByPositionByLevel(hotspot.position, 1).value
                .length > 0
            "
            v-for="virtualGroup in getVirtualGroupsByPositionByLevel(
              hotspot.position,
              1
            ).value"
            :show-sub-rows="
              !!virtualGroup.articleNumber &&
              checkDeepVirtualGroup(
                hotspot.position,
                virtualGroup.articleNumber
              ).value
            "
          >
            <!-- Deep Level Virtual Groups -->
            <patternlib-table-base
              slot="sub"
              v-if="
                !!virtualGroup.articleNumber &&
                checkDeepVirtualGroup(
                  hotspot.position,
                  virtualGroup.articleNumber
                )
              "
            >
              <patternlib-table-base-row>
                <patternlib-table-base-cell :colspan="999">
                  {{
                    getZPartsByPosition(hotspot.position).value.length > 0
                      ? t('spareparts.zPartInfoText')
                      : t('spareparts.virtualGroupInfoText')
                  }}
                  <br /><span style="font-weight: bold">{{
                    getZPartsByPosition(hotspot.position).value.length > 0
                      ? t('spareparts.zPartAutoAdd')
                      : t('spareparts.virtualGroupPackageHint')
                  }}</span>
                </patternlib-table-base-cell>
              </patternlib-table-base-row>

              <patternlib-table-base-row
                v-for="virtualElement in getVirtualGroupsByParent(
                  hotspot.position,
                  virtualGroup.articleNumber
                ).value"
              >
                <patternlib-table-base-cell
                  noBorder
                  :width="sizes ? sizes[0]?.toString() : undefined"
                ></patternlib-table-base-cell>
                <patternlib-table-base-cell
                  noBorder
                  :width="sizes ? sizes[1]?.toString() : undefined"
                  ><span v-html="getHighlightedName(virtualElement.name)"></span
                ></patternlib-table-base-cell>
                <patternlib-table-base-cell
                  noBorder
                  :colspan="2"
                  :width="sizes ? sizes[2]?.toString() : undefined"
                  ><span
                    v-html="
                      getHighlightedArticleNumber(virtualElement.articleNumber)
                    "
                  ></span>
                </patternlib-table-base-cell>
                <patternlib-table-base-cell
                  noBorder
                  :colspan="2"
                  :width="sizes ? sizes[3]?.toString() : undefined"
                ></patternlib-table-base-cell>
                <patternlib-table-base-cell
                  v-if="enablePrices"
                  noBorder
                  :width="sizes ? sizes[4]?.toString() : undefined"
                >
                  <div style="text-align: start">
                    {{
                      formatCurrency(
                        virtualElement.priceGross,
                        virtualElement.currency
                      )
                    }}
                  </div>
                </patternlib-table-base-cell>
                <patternlib-table-base-cell
                  noBorder
                  :width="sizes ? sizes[5]?.toString() : undefined"
                >
                  <patternlib-textinput
                    @vue:mounted="
                      hotspot.position && quantities?.set(hotspot.position, 1)
                    "
                    type="number"
                    max="999"
                    :value="
                      (quantities?.get(hotspot.position!) || 1) *
                      (virtualElement.quantity || 1)
                    "
                    readonly
                  >
                  </patternlib-textinput>
                </patternlib-table-base-cell>
                <patternlib-table-base-cell
                  noBorder
                  :width="sizes ? sizes[6]?.toString() : undefined"
                ></patternlib-table-base-cell>
              </patternlib-table-base-row>
            </patternlib-table-base>

            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[0]?.toString() : undefined"
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[1]?.toString() : undefined"
              ><span v-html="getHighlightedName(virtualGroup.name)"></span
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :colspan="2"
              :width="sizes ? sizes[2]?.toString() : undefined"
              ><span
                v-html="getHighlightedArticleNumber(virtualGroup.articleNumber)"
              ></span>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :colspan="2"
              :width="sizes ? sizes[3]?.toString() : undefined"
            ></patternlib-table-base-cell>
            <patternlib-table-base-cell
              v-if="enablePrices"
              noBorder
              :width="sizes ? sizes[4]?.toString() : undefined"
            >
              <div style="text-align: start">
                {{
                  formatCurrency(virtualGroup.priceGross, virtualGroup.currency)
                }}
              </div>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[5]?.toString() : undefined"
            >
              <patternlib-textinput
                @vue:mounted="
                  hotspot.position && quantities?.set(hotspot.position, 1)
                "
                type="number"
                max="999"
                :value="
                  (quantities?.get(hotspot.position!) || 1) *
                  (virtualGroup.quantity || 1)
                "
                readonly
              >
              </patternlib-textinput>
            </patternlib-table-base-cell>
            <patternlib-table-base-cell
              noBorder
              :width="sizes ? sizes[6]?.toString() : undefined"
            ></patternlib-table-base-cell>
          </patternlib-table-base-row>
        </patternlib-table-base>
      </patternlib-table-base-row>

      <div
        slot="footer"
        id="loading-bar"
        style="height: 60px; align-items: center; justify-items: center"
        v-show="!getAllHotspotsLoaded().value"
      >
        <patternlib-spinner
          size="lg"
          spin
          type="indeterminate"
          variant="light"
          v-if="getIsLoadingHotspots().value"
        >
        </patternlib-spinner>
      </div>
    </patternlib-table-base>
  </div>
</template>

<script setup lang="ts">
  import type { AxiosError } from 'axios';
  import { storeToRefs } from 'pinia';
  import {
    defineExpose,
    inject,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    type Ref
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { getColor } from '../helper/color';
  import { formatCurrency } from '../helper/currency';
  import { getLocalization } from '../localization/localizationService';
  import { useDataStore } from '../stores/data';
  import { useOrderStore, type ExtendedOrderPosition } from '../stores/order';

  const displayBreakpoint: Ref<string, string> | undefined =
    inject('displayBreakpoint');
  const createToast: any = inject('createToast');
  const sizes = ref<Array<number>>([]);
  const enablePrices = inject<boolean>('enablePrices');

  //non reactive => will only trigger once per change
  let appliedSearchFilter = '';

  const { t } = useI18n();
  const route = useRoute();
  const dataStore = useDataStore();
  const { quantities, serviceNumber, isMultiCompontsLoaded, searchFilter } =
    storeToRefs(dataStore);
  const {
    getZPartsByPosition,
    loadHotSpots,
    checkDeepVirtualGroup,
    setSearchFilter,
    getVirtualGroupsByPositionByLevel,
    getVirtualGroupsByParent,
    getHotSpots,
    getIsLoadingHotspots,
    getAllHotspotsLoaded,
    getPage,
    getPageSize
  } = dataStore;

  const orderStore = useOrderStore();
  const { addArticle } = orderStore;

  const header = useTemplateRef('header');
  const resizeObserver = new ResizeObserver(() => calculateRowSizes());

  const calculateRowSizes = () => {
    if (!header.value?.parentElement?.children) {
      return;
    }

    Array.from(header.value?.parentElement?.children).forEach(
      (htmlElement, index) => {
        sizes.value[index] = htmlElement.getBoundingClientRect().width;
        const styling = window.getComputedStyle(htmlElement, null);
        sizes.value[index] +=
          parseFloat(styling.paddingLeft) - parseFloat(styling.paddingRight);
      }
    );
  };

  const getHighlightedName = (name: string | null | undefined = '') => {
    if (!appliedSearchFilter) return name;
    const regex = new RegExp(`(${appliedSearchFilter})`, 'gi');
    return name?.replace(regex, '<mark>$1</mark>') || '';
  };

  const getHighlightedArticleNumber = (
    articleNumber: string | null | undefined = ''
  ) => {
    const articleNumberLabel =
      (displayBreakpoint?.value === 'isMobile' ? t('spareparts.artNr') : '') +
      ' ';
    if (!appliedSearchFilter) return articleNumberLabel + articleNumber;
    const regex = new RegExp(`(${appliedSearchFilter})`, 'gi');
    return (
      articleNumberLabel +
      (articleNumber?.replace(regex, '<mark>$1</mark>') || '')
    );
  };

  const getHighlightedPosition = (
    position: string | number | null | undefined = ''
  ) => {
    const positionText = position?.toString() || '';
    if (!appliedSearchFilter) return positionText;
    const regex = new RegExp(`(${appliedSearchFilter})`, 'gi');
    return positionText.replace(regex, '<mark>$1</mark>');
  };

  const loadNextHotspots = () => {
    if (!serviceNumber.value) return;
    if (getAllHotspotsLoaded().value) return;
    if (getIsLoadingHotspots().value) return;

    loadHotSpots(
      serviceNumber.value,
      getLocalization().value.Locale,
      getPage().value,
      getPageSize().value
    ).catch((err: AxiosError) => {
      createToast(err.message);
    });
  };

  const updateFilter = (filter: string) => {
    appliedSearchFilter = filter;
    setSearchFilter(filter, getLocalization().value.Locale);
  };

  const observeHeadSize = () => {
    //Resize Observer for table rows (z-parts/virtual products)
    const resizeTarget = document.getElementById('headerSparePart');
    if (resizeTarget) {
      resizeObserver.observe(resizeTarget);
    }
  };

  const waitForNextFrame = () =>
    new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });

  const waitForHotspotsLoadCycle = async () => {
    if (getAllHotspotsLoaded().value) {
      return;
    }

    let framesUntilLoadStarts = 60;

    while (
      !getAllHotspotsLoaded().value &&
      !getIsLoadingHotspots().value &&
      framesUntilLoadStarts > 0
    ) {
      await nextTick();
      await waitForNextFrame();
      framesUntilLoadStarts -= 1;
    }

    while (getIsLoadingHotspots().value) {
      await nextTick();
      await waitForNextFrame();
    }
  };

  const scrollToSparePart = async () => {
    //Check current Route for Serialnumber and ArticleNumber
    const articleNumberSparePart = route.hash.replace('#', '');
    if (!articleNumberSparePart) {
      return;
    }

    let sparePartDomElement = document.getElementById(articleNumberSparePart);
    while (!getAllHotspotsLoaded().value && !sparePartDomElement) {
      const intersectoinTarget = document.getElementById('loading-bar');
      if (!intersectoinTarget) {
        return;
      }

      intersectoinTarget.scrollIntoView({ behavior: 'smooth', block: 'end' });
      await waitForHotspotsLoadCycle();
      await nextTick();
      await waitForNextFrame();

      sparePartDomElement = document.getElementById(articleNumberSparePart);
    }

    if (sparePartDomElement) {
      await nextTick();
      const spanDomElement = sparePartDomElement.querySelector('span');

      if (spanDomElement) {
        spanDomElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }

      const nameDomElement = Array.from(sparePartDomElement.children).filter(
        (x) => x.id === 'name'
      );
      if (nameDomElement.length > 0) {
        nameDomElement[0].classList.add('highlighted');
      }
    }
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      const articleNumberSparePart = route.hash.replace('#', '');
      if (!articleNumberSparePart) {
        return;
      }

      let sparePartDomElement = document.getElementById(articleNumberSparePart);
      if (sparePartDomElement) {
        const nameDomElement = Array.from(sparePartDomElement.children).filter(
          (x) => x.id === 'name'
        );
        if (nameDomElement.length > 0) {
          nameDomElement[0].classList.remove('highlighted');
        }
      }
    }
  };

  onMounted(() => {
    //Intersection Observer for loading more Positions
    const intersectionTarget = document.getElementById('loading-bar');
    const options: IntersectionObserverInit = {
      root: document,
      rootMargin: '0px',
      threshold: 0
    };

    const intersectionObserver = new IntersectionObserver(
      () => getPage().value > 1 && loadNextHotspots(),
      options
    );
    intersectionObserver.observe(intersectionTarget!);

    window.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
  });

  defineExpose({
    scrollToSparePart
  });
</script>

<style scoped>
  #subTable {
    display: flex;
    width: 100%;
  }

  patternlib-table-base.isMobile::part(patternlib-table-base-table) {
    display: block;
    max-width: 100%;
    overflow-x: scroll;
  }

  html[breakpoint='isMobile']
    patternlib-table-base-row::part(patternlib-table-base-row) {
    height: auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(6, auto);
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    grid-auto-flow: row dense;
    padding: 5px;
  }

  html[breakpoint='isMobile']
    patternlib-table-base-cell::part(patternlib-table-base-cell-td) {
    height: max-content;
    grid-column: 1 / span 2;
  }

  html[breakpoint='isMobile']
    patternlib-table-base-cell:not(:nth-of-type(-n + 5))::part(
      patternlib-table-base-cell-td
    ) {
    grid-column: unset;
    height: max-content;
  }

  /* Overwrite top border => see figma */
  patternlib-table-base::part(patternlib-table-base-thead) {
    border-top: 1px solid var(--color-neutral-steel-300);
  }

  :deep(#name.highlighted span) {
    background-color: mark;
    color: marktext;
  }
</style>
